import { useCallback, useEffect, useMemo, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";

import { ChevronLeftIcon, ChevronRightIcon, FlagIcon } from "lucide-react";

import { OpenAPI } from "simplydo/interfaces";
import { RichTextViewer } from "simplydo/react/richtext";
import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Loader } from "simplydo/react/ui/loader";

import api from "api";
import { updateCurrentUser, useCurrentUser } from "utils/currentUser";

export const OnboardingContainer = styled.div`
  display: flex;
  flex-direction: column;
  &.is-preview {
    margin: 3rem;
  }

  .progress-labels {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2rem 0;
    > ${Label} {
      margin: 0 0.5rem;
    }
  }
  .agree-to-terms {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin-top: 1.5rem;
  }
`;

type ProgressLabelProps = { isActive: boolean; colour: string | null };

const ProgressLabel = ({ isActive, colour }: ProgressLabelProps) => (
  <Label
    circular
    color={!isActive ? "grey" : undefined}
    style={{ backgroundColor: isActive ? (colour ?? undefined) : undefined }}
  />
);

type CustomOnboardingProps = { isPreview?: boolean };

type OnboardingScreen = NonNullable<
  NonNullable<
    OpenAPI.GET<"/organisations/{id}/onboarding">["response"]["onboardingData"]
  >["screens"]
>[number];

type OnboardingProps = {
  isPreview?: boolean;
  organisationOnboarding: NonNullable<
    OpenAPI.GET<"/organisations/{id}/onboarding">["response"]["onboardingData"]
  >;
  completeOnboarding: () => void;
  completing: boolean;
};

export const Onboarding = ({
  isPreview,
  organisationOnboarding,
  completeOnboarding,
  completing,
}: OnboardingProps) => {
  const [userHasAgreedToScreen, setUserHasAgreedToScreen] = useState(false);
  const theme = useTheme();

  const [activePage, setActivePage] = useState(0);

  useEffect(() => {
    setUserHasAgreedToScreen(false);
  }, []);

  const screens = useMemo(
    () => organisationOnboarding.screens ?? [],
    [organisationOnboarding.screens],
  );

  const nextPageAvailable = activePage < screens.length - 1;
  const prevPageAvailable = activePage !== 0;

  const currentScreen = useMemo(
    () => screens[activePage],
    [screens, activePage],
  );

  const userCanProgress = useMemo(
    () =>
      currentScreen &&
      (!currentScreen.requireUserAgreement ||
        (currentScreen.requireUserAgreement && userHasAgreedToScreen)),
    [currentScreen, userHasAgreedToScreen],
  );

  return (
    <OnboardingContainer className={cx(isPreview && "is-preview")}>
      <div style={{ maxWidth: "100%", overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            position: "relative",
            left: `-${activePage * 100}%`,
            transition: "left 0.5s",
          }}
        >
          {screens.map((screen: OnboardingScreen, index: number) => (
            <div
              key={index}
              style={{
                maxWidth: "100%",
                flex: "1 0 100%",
                maxHeight: 300,
                overflow: "auto",
              }}
            >
              <RichTextViewer value={screen.content} />
            </div>
          ))}
        </div>
      </div>
      <div
        className="agree-to-terms"
        style={{
          visibility: !currentScreen?.requireUserAgreement
            ? "hidden"
            : "visible",
        }}
      >
        <Checkbox
          checked={userHasAgreedToScreen}
          onClick={() => setUserHasAgreedToScreen(!userHasAgreedToScreen)}
        >
          I have read and agree to the above terms.
        </Checkbox>
      </div>
      <div className="progress-labels">
        {prevPageAvailable ? (
          <Button
            labelPosition="left"
            onClick={() => setActivePage((prevPage) => prevPage - 1)}
          >
            <Icon icon={ChevronLeftIcon} />
            Prev
          </Button>
        ) : null}
        {nextPageAvailable ? (
          <Button
            labelPosition="right"
            onClick={() => setActivePage((prevPage) => prevPage + 1)}
            disabled={!userCanProgress}
            color="primary"
          >
            Next
            <Icon icon={ChevronRightIcon} />
          </Button>
        ) : null}
        {!nextPageAvailable && !isPreview ? (
          <Button
            labelPosition="right"
            onClick={completeOnboarding}
            loading={completing}
            disabled={!userCanProgress}
            color="primary"
          >
            Finish
            <Icon icon={FlagIcon} />
          </Button>
        ) : null}
      </div>
      <div className="progress-labels">
        {screens.map((_screen: OnboardingScreen, index: number) => (
          <ProgressLabel
            isActive={activePage === index}
            colour={theme?.primaryColour}
            key={index}
          />
        ))}
      </div>
    </OnboardingContainer>
  );
};

const CustomOnboarding = ({ isPreview }: CustomOnboardingProps) => {
  const user = useCurrentUser();

  const [completing, setCompleting] = useState(false);
  const organisationId = user?.ownerOrganisation?._id;
  const [organisationOnboarding, setOrganisationOnboarding] = useState<
    | OpenAPI.GET<"/organisations/{id}/onboarding">["response"]["onboardingData"]
    | null
  >(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organisationId) {
      setLoading(false);
      return undefined;
    }
    let isMounted = true;
    setLoading(true);
    api.organisations.getOnboarding(
      organisationId,
      ({ onboardingData, found }) => {
        if (!isMounted) return;

        setLoading(false);
        if (found) {
          setOrganisationOnboarding(onboardingData);
        }
      },
      (err) => {
        if (!isMounted) return;

        toast.error(err.message);
        setLoading(false);
      },
    );
    return () => {
      isMounted = false;
    };
  }, [organisationId]);

  const completeOnboarding = useCallback(() => {
    if (!organisationId) return;
    setCompleting(true);
    api.organisations.trackOnboardingCompletion(
      organisationId,
      () => {
        updateCurrentUser({ hasCompletedOrganisationOnboarding: true });
        setCompleting(false);
      },
      (err) => {
        toast.error(err.message);
        setCompleting(false);
      },
    );
  }, [organisationId]);

  if (loading) {
    return <Loader />;
  }
  if (!organisationOnboarding?.screens?.length) return null;
  return (
    <>
      {!isPreview && user?.ownerOrganisation ? (
        <Helmet title={`Welcome to ${user.ownerOrganisation.name}`} />
      ) : null}
      <Onboarding
        isPreview={isPreview}
        organisationOnboarding={organisationOnboarding}
        completeOnboarding={completeOnboarding}
        completing={completing}
      />
    </>
  );
};

export default CustomOnboarding;
